import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { FiArrowLeft, FiCheckCircle, FiAlertCircle, FiActivity } from 'react-icons/fi';
import { MdPets } from 'react-icons/md';

const CampusReadyPage: React.FC = () => {
  const navigate = useNavigate();
  const [clearanceStatus] = useState<'cleared' | 'pending'>('cleared');

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center text-uc-navy hover:text-uc-gold transition-colors mb-4"
          >
            <FiArrowLeft size={20} className="mr-2" />
            Back to Home
          </button>
          <h1 className="text-3xl font-bold text-uc-navy font-header">CAMPUS READY</h1>
          <p className="text-gray-600 mt-2">Health screening and campus access clearance</p>
        </div>

        {/* Clearance Status */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <MdPets className="text-uc-gold" size={32} />
            <h2 className="text-xl font-bold text-uc-navy">Today's Clearance Status</h2>
          </div>

          {clearanceStatus === 'cleared' ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <FiCheckCircle className="text-green-600 flex-shrink-0" size={48} />
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-green-900 mb-2">Cleared for Campus</h3>
                  <p className="text-green-800 mb-4">
                    You are cleared to access campus facilities today. Show this screen if requested.
                  </p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-green-700 font-medium">Valid Until:</p>
                      <p className="text-green-900">11:59 PM Today</p>
                    </div>
                    <div>
                      <p className="text-green-700 font-medium">Screening Date:</p>
                      <p className="text-green-900">{new Date().toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <FiAlertCircle className="text-yellow-600 flex-shrink-0" size={48} />
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-yellow-900 mb-2">Screening Pending</h3>
                  <p className="text-yellow-800 mb-4">
                    Please complete your daily health screening to access campus.
                  </p>
                  <button className="bg-yellow-600 text-white px-6 py-2 rounded-lg hover:bg-yellow-700 transition-colors font-semibold">
                    Complete Screening
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Health Screening Questions */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-uc-navy mb-4">Daily Health Screening</h2>
          <p className="text-gray-600 mb-4">
            Complete the following questions each day before coming to campus:
          </p>
          <div className="space-y-3">
            <div className="p-4 border border-gray-200 rounded-lg">
              <p className="text-gray-700">1. Do you have any COVID-19 symptoms?</p>
            </div>
            <div className="p-4 border border-gray-200 rounded-lg">
              <p className="text-gray-700">2. Have you tested positive for COVID-19 in the past 10 days?</p>
            </div>
            <div className="p-4 border border-gray-200 rounded-lg">
              <p className="text-gray-700">3. Have you been in close contact with someone who tested positive?</p>
            </div>
          </div>
        </div>

        {/* Vaccination Status */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <FiActivity className="text-uc-gold" size={24} />
            <h2 className="text-xl font-bold text-uc-navy">Vaccination Status</h2>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-blue-900">COVID-19 Vaccination</p>
                <p className="text-sm text-blue-700">Up to date</p>
              </div>
              <FiCheckCircle className="text-blue-600" size={32} />
            </div>
          </div>
        </div>

        {/* Resources */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-bold text-uc-navy mb-4">Campus Health Resources</h3>
          <div className="space-y-3 text-gray-700">
            <p>
              UC Merced is committed to maintaining a healthy campus environment. Resources include:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Daily health screening system</li>
              <li>On-campus COVID-19 testing</li>
              <li>Vaccination clinics and boosters</li>
              <li>Contact tracing support</li>
              <li>Mental health and wellness resources</li>
              <li>Student Health Services appointments</li>
            </ul>
            <p className="text-sm text-gray-600 mt-4">
              For health-related questions or concerns, contact Student Health Services at (209) 228-2273
              or visit the Student Health Center.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CampusReadyPage;
